import { useState, useRef, useCallback, useEffect } from "react"
import { router, usePage } from "@inertiajs/react"
import { track } from "@/lib/analytics"
import type { Listing, SharedProps, SocialConnection } from "@/types"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import PlatformCaptionTabs from "@/components/dashboard/posts/platform-caption-tabs"
import LocationPicker from "@/components/dashboard/posts/location-picker"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import { OptimizedImage } from "@/components/ui/optimized-image"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
type Platform = "instagram" | "facebook"

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
}

export default function ShareListingDialog({
  listing,
  connections,
}: {
  listing: Listing
  connections: SocialConnection[]
}) {
  const { currentUser } = usePage<SharedProps>().props
  const [open, setOpen] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set())
  const [captions, setCaptions] = useState<Record<Platform, string>>({
    instagram: "",
    facebook: "",
  })
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined)
  const [publishing, setPublishing] = useState(false)
  const [generatingCaption, setGeneratingCaption] = useState<Record<string, boolean>>({})
  const [facebookPageId, setFacebookPageId] = useState<string>("")
  const [locationId, setLocationId] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)
  const [locationMeta, setLocationMeta] = useState<{ address?: string; verified?: boolean; picture_url?: string; hours?: string } | null>(null)

  const fbConnection = connections.find(
    (c) => c.platform === "facebook" && c.status === "active",
  )
  const fbPages = fbConnection?.managed_pages || []

  const supportedConnections = connections.filter(
    (c) => (c.platform === "facebook" || c.platform === "instagram") && c.status === "active",
  )

  function togglePlatform(platform: Platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  async function handlePublish() {
    if (selectedPlatforms.size === 0) return

    setPublishing(true)
    try {
      const captionsObj: Record<string, string> = {}
      for (const p of selectedPlatforms) {
        captionsObj[p] = captions[p]
      }

      const response = await fetch(
        `/dashboard/listings/${listing.slug}/social_posts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({
            platforms: Array.from(selectedPlatforms),
            captions: captionsObj,
            scheduled_at: scheduleEnabled && scheduledAt ? scheduledAt.toISOString() : null,
            listing_image_signed_id: selectedImageId,
            facebook_page_id: selectedPlatforms.has("facebook") && facebookPageId ? facebookPageId : null,
            location_id: locationId,
            location_name: locationName,
            location_address: locationMeta?.address || null,
            location_verified: locationMeta?.verified || null,
            location_picture_url: locationMeta?.picture_url || null,
            location_hours: locationMeta?.hours || null,
          }),
        },
      )

      if (response.ok) {
        track("listing_post_published", {
          platforms: Array.from(selectedPlatforms),
          scheduled: scheduleEnabled,
          listing_slug: listing.slug,
        })
        toast.success(
          scheduleEnabled
            ? "Posts scheduled successfully"
            : "Publishing to social media...",
        )
        setOpen(false)
        router.visit("/dashboard/posts")
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to create posts")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setPublishing(false)
    }
  }

  async function handleGenerateCaption(platform: string) {
    setGeneratingCaption((prev) => ({ ...prev, [platform]: true }))
    try {
      const response = await fetch(
        `/dashboard/listings/${listing.slug}/social_posts/generate_caption`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({ platform }),
        },
      )

      if (response.ok) {
        const data = await response.json()
        setCaptions((prev) => ({ ...prev, [platform]: data.caption }))
        track("listing_caption_generated", { platform, listing_slug: listing.slug })
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to generate caption")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setGeneratingCaption((prev) => ({ ...prev, [platform]: false }))
    }
  }

  const hasConnections = supportedConnections.length > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-red text-white hover:bg-deep-red">
          <svg
            className="mr-1.5 size-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
            />
          </svg>
          Post
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share listing</DialogTitle>
          <DialogDescription>
            Post this listing to your social media channels.
          </DialogDescription>
        </DialogHeader>

        {!hasConnections ? (
          <NoConnectionsCallout />
        ) : (<>

        {/* Image selection filmstrip */}
        {listing.images_data.length > 0 && (
          <ImageSelectFilmstrip
            images={listing.images_data}
            selectedImageId={selectedImageId}
            onSelect={(id) => setSelectedImageId(selectedImageId === id ? null : id)}
          />
        )}

        {/* Platform selection */}
        <div className="space-y-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Platforms
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(["facebook", "instagram"] as Platform[]).map((platform) => {
              const connection = supportedConnections.find(
                (c) => c.platform === platform,
              )
              const isSelected = selectedPlatforms.has(platform)
              const disabled = !connection

              return (
                <button
                  key={platform}
                  type="button"
                  onClick={() => !disabled && togglePlatform(platform)}
                  disabled={disabled}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                    disabled
                      ? "cursor-not-allowed border-stone/40 bg-stone/5 text-warm-grey/40"
                      : isSelected
                        ? "border-red/50 bg-red/5 text-charcoal"
                        : "border-stone/60 text-warm-grey hover:border-stone"
                  }`}
                >
                  <span
                    className={`flex size-4 items-center justify-center rounded border text-[10px] ${
                      isSelected
                        ? "border-red bg-red text-white"
                        : "border-stone/70"
                    }`}
                  >
                    {isSelected && (
                      <svg className="size-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    )}
                  </span>
                  <span className="font-medium">
                    {PLATFORM_LABELS[platform]}
                  </span>
                  {!connection && (
                    <span className="ml-auto text-[10px] text-warm-grey/50">
                      Not connected
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Captions */}
        {selectedPlatforms.size > 0 && (
          <PlatformCaptionTabs
            platforms={Array.from(selectedPlatforms)}
            captions={captions}
            onCaptionChange={(platform, value) =>
              setCaptions((prev) => ({ ...prev, [platform]: value }))
            }
            onGenerateCaption={handleGenerateCaption}
            generatingCaption={generatingCaption}
            facebookPages={fbPages}
            facebookPageId={facebookPageId}
            onFacebookPageChange={setFacebookPageId}
          />
        )}

        {/* Location tag — superadmin only */}
        {currentUser?.is_superadmin && selectedPlatforms.size > 0 && (
          <LocationPicker
            locationId={locationId}
            locationName={locationName}
            locationMeta={locationMeta}
            onLocationChange={(id, name, meta) => {
              setLocationId(id)
              setLocationName(name)
              setLocationMeta(meta ?? null)
            }}
            lat={listing.latitude}
            lng={listing.longitude}
            disabled={supportedConnections.length === 0}
          />
        )}

        {/* Schedule toggle */}
        {selectedPlatforms.size > 0 && (
          <div className="space-y-3 border-t border-stone/20 pt-4">
            <div className="flex items-center gap-2">
              <Switch
                id="listing-schedule-toggle"
                checked={scheduleEnabled}
                onCheckedChange={setScheduleEnabled}
              />
              <Label htmlFor="listing-schedule-toggle" className="text-sm">
                Schedule for later
              </Label>
            </div>

            {scheduleEnabled && (
              <DateTimePicker
                value={scheduledAt}
                onChange={setScheduledAt}
                minDate={new Date()}
                placeholder="Choose date & time"
                className="w-auto"
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={
              publishing ||
              selectedPlatforms.size === 0 ||
              (scheduleEnabled && !scheduledAt)
            }
            className="bg-red px-6 text-white hover:bg-red/90"
          >
            {publishing
              ? "Publishing..."
              : scheduleEnabled
                ? "Schedule"
                : `Share to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>

        </>)}
      </DialogContent>
    </Dialog>
  )
}

function NoConnectionsCallout() {
  return (
    <div className="rounded-lg border border-stone/30 bg-stone/5 px-4 py-5 text-center">
      <svg className="mx-auto mb-2 size-8 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
      <p className="text-sm font-medium text-charcoal">No social accounts connected</p>
      <p className="mt-1 text-xs text-warm-grey">
        Connect your social media accounts to start publishing.
      </p>
      <a
        href="/dashboard/connections"
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-deep-red"
      >
        Connect accounts
        <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
        </svg>
      </a>
    </div>
  )
}

function ImageSelectFilmstrip({
  images,
  selectedImageId,
  onSelect,
}: {
  images: Array<{ signed_id: string; url: string; thumb_url: string }>
  selectedImageId: string | null
  onSelect: (signedId: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    checkScroll()
    el.addEventListener("scroll", checkScroll, { passive: true })
    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)
    return () => {
      el.removeEventListener("scroll", checkScroll)
      observer.disconnect()
    }
  }, [checkScroll])

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.7
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" })
  }

  return (
    <div className="space-y-2 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
        Select image (optional)
      </p>
      <div className="group/strip relative">
        <div
          ref={scrollRef}
          className="flex h-28 gap-1.5 overflow-x-auto scroll-smooth rounded-lg scrollbar-none"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {images.map((img, i) => {
            const isSelected = selectedImageId === img.signed_id
            return (
              <button
                key={img.signed_id}
                type="button"
                onClick={() => onSelect(img.signed_id)}
                className={`group/img relative h-full shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                  isSelected
                    ? "border-red ring-1 ring-red/30"
                    : "border-transparent hover:border-stone/40"
                }`}
                style={{
                  scrollSnapAlign: "start",
                  width: "clamp(100px, 25%, 160px)",
                }}
              >
                <OptimizedImage
                  src={img.thumb_url}
                  alt={`Listing image ${i + 1}`}
                  className="size-full object-cover transition-transform duration-300 group-hover/img:scale-105"
                />
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center bg-red/10">
                    <svg className="size-5 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </div>
                )}
                <span className="absolute right-1 top-1 rounded bg-charcoal/60 px-1 py-0.5 text-[9px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
                  {i + 1}/{images.length}
                </span>
              </button>
            )
          })}
        </div>

        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute left-1.5 top-1/2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
          >
            <svg className="size-3 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute right-1.5 top-1/2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
          >
            <svg className="size-3 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}

        {canScrollLeft && (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 rounded-l-lg bg-gradient-to-r from-white/80 to-transparent" />
        )}
        {canScrollRight && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-lg bg-gradient-to-l from-white/80 to-transparent" />
        )}
      </div>
    </div>
  )
}
